import { Injectable } from '@angular/core';
import { AuthService } from '@gelato-api-ui/core/auth/auth.service';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { ActivatedRouteSnapshot } from '@angular/router';
import { LastRequestedUrlService } from '@api-ui-app/src/app/shared/services/last-requested-url.service';
import { UserRouteCanActivateResult } from '@gelato-api-ui/core/auth/types/user-route-can-activate-result';
import { environment } from '@api-ui-app/src/environments/environment';
import { CheckoutOrderIdService } from '@api-ui-app/src/app/orders/services/checkout-order-id.service';
import { isNotNil } from '@gelato-api-ui/utils/ramda';

@Injectable({ providedIn: 'root' })
export class UserActionAvailabilityService {
  private guestAllowedRoutes = [/^\/catalogue\/print-file-editor/, /^\/checkout/];

  private isGuest$ = this.auth.isGuest();
  private isNotGuest$ = this.isGuest$.pipe(map(isGuest => !isGuest));

  showSidebar$ = this.isNotGuest$;
  showHelloBar$ = this.isNotGuest$;
  externalLogoLink$ = this.auth.isGuest().pipe(map(isGuest => (isGuest ? environment.baseUrls.gelatoCom : null)));

  constructor(
    private readonly auth: AuthService,
    private readonly lastRequestedUrlService: LastRequestedUrlService,
    private readonly checkoutOrderIdService: CheckoutOrderIdService,
  ) {}

  routeCanActivate(route: ActivatedRouteSnapshot, currentUrl: string): Observable<UserRouteCanActivateResult> {
    return combineLatest([this.auth.isActivated(), this.auth.getUserId(), this.auth.isGuest()]).pipe(
      take(1),
      switchMap(([activated, userId, isGuest]) => {
        if (isGuest) {
          return this.guestRouteCanActivate(route, currentUrl);
        }

        if (activated === false) {
          if (userId) {
            return this.auth.activateUser().pipe(
              switchMap(() => this.auth.isGelatoUser()),
              map(isGelatoUser => {
                this.onActivated(route, isGelatoUser);
                return { canActivate: true };
              }),
            );
          } else {
            this.lastRequestedUrlService.saveActivatedRouteSnapshot(route, this.lastRequestedUrlService.activationKey);
            return of({
              canActivate: false,
              redirect: { fragments: ['/on-boarding'], params: { queryParams: route.queryParams } },
            });
          }
        }

        return of({ canActivate: true });
      }),
    );
  }

  private onActivated(route: ActivatedRouteSnapshot, isGelatoUser: boolean): void {
    // admin user that came from gelato.com should not add product to existing order
    if (isGelatoUser && route.queryParams['from-landing']) {
      this.auth
        .getClientId()
        .pipe(filter(isNotNil), take(1))
        .subscribe(id => this.checkoutOrderIdService.remove(id));
    }
  }

  private guestRouteCanActivate(
    route: ActivatedRouteSnapshot,
    currentUrl: string,
  ): Observable<UserRouteCanActivateResult> {
    const isUrlAllowed = this.guestAllowedRoutes.some(urlRegEx => urlRegEx.test(currentUrl));
    const isStartSelling = route.queryParams['startSelling'];

    if (isStartSelling) {
      this.auth.signOut();
      return of({ canActivate: false });
    }

    return this.auth.activateUser().pipe(
      switchMap(() => this.auth.isActivated()),
      filter(activated => activated),
      take(1),
      map(
        (): UserRouteCanActivateResult => ({
          canActivate: isUrlAllowed,
          redirect: {
            fragments: ['/checkout'],
            params: { queryParams: route.queryParams },
          },
        }),
      ),
    );
  }
}
